import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { AnyAction } from 'redux'
import axios from 'axios';
import { appConfig } from '../../config';
import api from '../globalAxios';
import reduxStore from '..';
import {
    classifyMeError,
    ME_RETRY_BACKOFF_MS,
    redirectToLoginAfterAuthFailure,
    sleep,
} from '../../utils/authSessionUtils';
import { authUserSnapshotEqual } from '../../utils/authProfileUtils';
import { recordBackgroundPoll } from '../../utils/backgroundSyncMetrics';

import { setAlert } from '../alert/actions';
import { clearCollectionMastersCache } from '../collections/collectionMastersCache';
import { clearAllCollabStorageKeys } from '../../utils/orderCollabStorage';
import { clearStoredOrderId } from '../../utils/orderRouteUtils';
import {
    clearActiveOrder,
    endCollabMode,
    setCollabSyncUi,
    DEFAULT_COLLAB_SYNC_UI,
} from '../order/actions';

const baseUrl = appConfig.api.baseUrl;
const ME_MAX_ATTEMPTS = 3;
const SESSION_MAX_ATTEMPTS = 2;

let lastSessionTransientAlertAt = 0;
let profileRefreshInFlight: Promise<void> | null = null;
let sessionProbeInFlight: Promise<void> | null = null;

const hasExistingUserProfile = (): boolean => {
    const userSlice = reduxStore.getState().user.user;
    return Boolean(userSlice && userSlice.user);
};

const parseMeResponseData = (data: any): AuthUser => {
    let disallowedTypes: string[] = [];

    if (data && data.disallowTypes) {
        const types = JSON.parse(data.disallowTypes);
        disallowedTypes = types.map((type: any) => type.OrdertypeType.toLowerCase());
    }

    return {
        currencyCode: data.currencyCode,
        customerData: JSON.parse(data.customer),
        email: data.login,
        disallowedTypes,
        showUsSizes: parseShowUsSizes(data),
    };
};

const handleHardAuthFailure = (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
    clearSessionOrderState(dispatch);
    if (process.env.NODE_ENV !== 'production') {
        localStorage.removeItem('user');
        clearLocalStorage();
    }
    dispatch(userIsvalidError());
    redirectToLoginAfterAuthFailure();
};

const maybeSessionTransientAlert = (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
    const now = Date.now();
    if (now - lastSessionTransientAlertAt < 60000) {
        return;
    }
    lastSessionTransientAlertAt = now;
    dispatch(setAlert('Connection is unstable. Your session is still active.', 'error'));
};

const clearLocalStorage = () => {
    const keysToRemove = [
        'previousNonDashboardUrl',
        'order',
        'recentClosedOrders',
        'token',
        'search:prevPage'
    ];

    for (const key of keysToRemove) {
        if (localStorage.getItem(key)) {
            localStorage.removeItem(key);
        }
    }
    clearStoredOrderId();
    clearAllCollabStorageKeys();
    clearCollectionMastersCache();
}

const clearSessionOrderState = (dispatch: ThunkDispatch<{}, {}, AnyAction>) => {
    dispatch(clearActiveOrder());
    dispatch(endCollabMode());
    dispatch(setCollabSyncUi(DEFAULT_COLLAB_SYNC_UI));
    clearStoredOrderId();
    clearAllCollabStorageKeys();
}

export interface ILoginUserToken {
    type: 'LOGIN_USER_SUCCESS',
    token: string
}

/** From Authenticate or Me / UpdateSizeDisplay responses (camelCase JSON). */
export function parseShowUsSizes(data: { showUsSizes?: boolean } | null | undefined): boolean {
    return data && data.showUsSizes === true;
}

export interface ILoginUserError {
    type: 'LOGIN_USER_ERROR',
    error: string
}

export interface ILogoutUser {
    type: 'LOGOUT_USER_SUCCESS',
}

export interface IIsUserValid {
    type: 'USER_IS_VALID_SUCCESS',
    user: AuthUser,
}

export interface IIsUserValidError {
    type: 'USER_IS_VALID_ERROR',
    isValid: boolean,
}

export type UserActions = ILoginUserToken | IIsUserValid | IIsUserValidError | ILogoutUser | ILoginUserError | IUserSizeDisplayUpdated | IUserLoginPreferencesLoaded

export interface IUser {
    email: string
    password: string
}

export interface AuthUser {
    currencyCode: string
    customerData: any
    email: string,
    disallowedTypes: string[]
    showUsSizes: boolean
}

export interface IUserSizeDisplayUpdated {
    type: 'USER_SIZE_DISPLAY_UPDATED',
    showUsSizes: boolean
}

export interface IUserLoginPreferencesLoaded {
    type: 'USER_LOGIN_PREFERENCES_LOADED',
    showUsSizes: boolean
}

export const userLoginPreferencesLoaded = (showUsSizes: boolean): IUserLoginPreferencesLoaded => ({
    type: 'USER_LOGIN_PREFERENCES_LOADED',
    showUsSizes,
})

// Action Creators
export const loginUserSuccess = (token: string): ILoginUserToken => {
    return { type: 'LOGIN_USER_SUCCESS', token }
}

export const loginUserError = (error: string): ILoginUserError => {
    return { type: 'LOGIN_USER_ERROR', error }
}

export const logoutUserSuccess = (): ILogoutUser => {
    return { type: 'LOGOUT_USER_SUCCESS' }
}

export const userIsvalidSucces = (user: AuthUser): IIsUserValid => {
    return { type: 'USER_IS_VALID_SUCCESS', user }
}

export const userIsvalidError = (): IIsUserValidError => {
    return { type: 'USER_IS_VALID_ERROR', isValid: false }
}

export const loginUser = (email: string, password: string): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
        // dispatch(alertClear());
        try {
            const response = await UserApi.loginUser(email, password);
            
            if (process.env.NODE_ENV !== 'production') {
                localStorage.setItem('token',response.data.token);
            }
            clearStoredOrderId();
            clearAllCollabStorageKeys();
            dispatch(loginUserSuccess(response.data.token));
            // showUsSizes comes on the login payload; profile load (Me) runs from Login.tsx
            dispatch(userLoginPreferencesLoaded(parseShowUsSizes(response.data)));
        } catch (error) {
            if (error && error.response && error.response.status === 429) {
                dispatch(setAlert('Too many login attempts. Please try again in 15 minutes.', 'error'));
                return;
            }
            dispatch(setAlert('The user name or password provided is incorrect.', 'error'))
            // dispatch(setAlert(error.response.data.message, 'error'));
        }
    }
}

const resolveDevToken = (): string | null | undefined => {
    if (process.env.NODE_ENV !== 'production') {
        return localStorage.getItem('token');
    }
    return undefined;
};

const ensureDevTokenOrLogout = (dispatch: ThunkDispatch<{}, {}, AnyAction>): string | null | undefined => {
    const devToken = resolveDevToken();
    if (process.env.NODE_ENV !== 'production' && !devToken) {
        clearSessionOrderState(dispatch);
        dispatch(userIsvalidError());
        localStorage.removeItem('user');
        clearLocalStorage();
        return null;
    }
    return devToken;
};

const runProfileRefresh = async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
    const devToken = ensureDevTokenOrLogout(dispatch);
    if (devToken === null) {
        return;
    }

    for (let attempt = 0; attempt < ME_MAX_ATTEMPTS; attempt++) {
        try {
                const response = await UserApi.fetchUserProfile(devToken);
                recordBackgroundPoll('Users/Me');
                const nextUser = parseMeResponseData(response.data);
            const currentProfile = reduxStore.getState().user.user.user;
            const alreadyValidated = reduxStore.getState().user.user.isValidated;

            if (alreadyValidated && authUserSnapshotEqual(currentProfile, nextUser)) {
                return;
            }

            dispatch(userIsvalidSucces(nextUser));
            return;
        } catch (e) {
            const kind = classifyMeError(e);
            if (kind === 'hard') {
                handleHardAuthFailure(dispatch);
                return;
            }
            if (attempt < ME_MAX_ATTEMPTS - 1) {
                await sleep(ME_RETRY_BACKOFF_MS[attempt] != null ? ME_RETRY_BACKOFF_MS[attempt] : 2000);
            }
        }
    }

    if (hasExistingUserProfile()) {
        maybeSessionTransientAlert(dispatch);
        return;
    }

    handleHardAuthFailure(dispatch);
};

/** Full Users/Me — login bootstrap, profile + ShowUsSizes refresh. */
export const refreshUserProfile = (): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
        if (profileRefreshInFlight) {
            return profileRefreshInFlight;
        }
        profileRefreshInFlight = runProfileRefresh(dispatch).finally(() => {
            profileRefreshInFlight = null;
        });
        return profileRefreshInFlight;
    };
};

/** Lightweight Users/Session — auth keepalive only (no DB). */
export const probeAuthSession = (): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
        const userSlice = reduxStore.getState().user.user;
        if (!userSlice.isValidated) {
            return refreshUserProfile()(dispatch);
        }

        if (sessionProbeInFlight) {
            return sessionProbeInFlight;
        }

        sessionProbeInFlight = (async () => {
            const devToken = ensureDevTokenOrLogout(dispatch);
            if (devToken === null) {
                return;
            }

            for (let attempt = 0; attempt < SESSION_MAX_ATTEMPTS; attempt++) {
                try {
                    await UserApi.checkAuthSession(devToken);
                    recordBackgroundPoll('Users/Session');
                    return;
                } catch (e) {
                    const kind = classifyMeError(e);
                    if (kind === 'hard') {
                        handleHardAuthFailure(dispatch);
                        return;
                    }
                    if (attempt < SESSION_MAX_ATTEMPTS - 1) {
                        await sleep(ME_RETRY_BACKOFF_MS[0]);
                    }
                }
            }

            if (!hasExistingUserProfile()) {
                await refreshUserProfile()(dispatch);
            }
        })().finally(() => {
            sessionProbeInFlight = null;
        });

        return sessionProbeInFlight;
    };
};

/** @deprecated Use refreshUserProfile — kept for Login and existing imports. */
export const isUserValid = refreshUserProfile;

export const logoutUser = (): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {   
        try {
            console.log('logout called')
            await UserApi.logoutUser();
            clearLocalStorage();
            clearSessionOrderState(dispatch);
            dispatch(logoutUserSuccess())
        } catch (e) {
            clearLocalStorage();
            clearSessionOrderState(dispatch);
            dispatch(logoutUserSuccess())
        }
    }
}

export const userSizeDisplayUpdated = (showUsSizes: boolean): IUserSizeDisplayUpdated => ({
    type: 'USER_SIZE_DISPLAY_UPDATED',
    showUsSizes,
})

export const updateSizeDisplay = (showUsSizes: boolean): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
        try {
            const response = await UserApi.updateSizeDisplay(showUsSizes);
            dispatch(userSizeDisplayUpdated(parseShowUsSizes(response.data)));
        } catch (e) {
            dispatch(setAlert('Could not save size display preference.', 'error'));
        }
    }
}

export const updatePassword = (email: string, oldPassword: string, newPassword: string): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
    return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {   
        try {
            const update = await UserApi.updatePassword(email, oldPassword, newPassword);

            if (update) {
                dispatch(setAlert('Your password has been updated!', 'success'))
                dispatch(isUserValid())
            }            
        } catch (e) {}
    }
}

// API.
export class UserApi {
    static loginUser = async (email: string, password: string) => {
        const url = baseUrl + 'Users/authenticate';
        return await axios.post(url, {email, password});
    }

    static fetchUserProfile = async (devToken?: string | null) => {
        const url = baseUrl + 'Users/Me';
        const headers = devToken ? { Authorization: 'bearer ' + devToken } : undefined;

        return await api.post(url, null, { withCredentials: true, headers });
    }

    static checkAuthSession = async (devToken?: string | null) => {
        const url = baseUrl + 'Users/Session';
        const headers = devToken ? { Authorization: 'bearer ' + devToken } : undefined;

        return await api.post(url, null, { withCredentials: true, headers });
    }

    static isUserValid = async (devToken?: string | null) => UserApi.fetchUserProfile(devToken);

    static logoutUser = async () => {
        const url = baseUrl + 'Users/Logout';
        return await api.post(url, null, { withCredentials: true });
    }

    static resetPassword = async (email: string) => {
        const url = baseUrl + 'Users/ResetPassword';
        return await axios.post(url, null, {params: { Email: email }});
    }

    static updatePassword = async (email: string, oldPassword: string, newPassword: string) => {
        const url = baseUrl + 'Users/UpdatePassword';
        return await axios.post(url, null, {params: {
            email,
            oldPassword,
            newPassword,
        }})
    }

    static updateSizeDisplay = async (showUsSizes: boolean) => {
        const url = baseUrl + 'Users/UpdateSizeDisplay';
        return await axios.post(url, { showUsSizes });
    }
}